import axios from "axios";
import { ImportType, JsxElement, JsxElementType } from "./jsxElementTypes";
import { NavTreeNode } from "./types";
import { toast } from "sonner";
import { ReactFramework } from "./templateProjects";

// Matches in constructNewComponent.tsx in tempo-api
export enum ExtractedPropType {
  LITERAL = "LITERAL",
  FUNCTION = "FUNCTION",
  JSON_OBJECT = "JSON_OBJECT",
}

export type ExtractedProp = {
  type: ExtractedPropType;
  name: string;
  value: any;
};

export enum ExtractedElementType {
  JSX = "JSX",
  LITERAL = "LITERAL",
  FRAGMENT = "FRAGMENT",
}

export type ExtractedElement = {
  type: ExtractedElementType;

  // One of these two must be set
  literalValue?: any;
  treeElement?: JsxElement;

  props?: ExtractedProp[];
  children?: ExtractedElement[];
};

export const getExtractedElement = (
  navTreeNode: NavTreeNode,
  treeElementLookup: { [codebaseId: string]: JsxElement }
): {
  extractedElement?: ExtractedElement;
  error?: string;
} => {
  if (!navTreeNode?.tempoElement?.codebaseId) {
    return {
      error: "No codebase element found for selected node",
    };
  }

  const fileForNavTreeNode =
    treeElementLookup[navTreeNode.tempoElement.codebaseId]?.filename;
  if (!fileForNavTreeNode) {
    return {
      error: "No filename found for selected node",
    };
  }

  const getExtractedElementForNode = (node: NavTreeNode): ExtractedElement => {
    const treeElementForNode = treeElementLookup[node.tempoElement.codebaseId];
    if (
      !treeElementForNode ||
      treeElementForNode.filename !== fileForNavTreeNode
    ) {
      // @ts-expect-error TS2322
      return null;
    }

    let newElement: ExtractedElement = {
      type: ExtractedElementType.JSX,
      treeElement: treeElementForNode,
      props: Object.keys(node.props || {}).map((propName) => {
        // @ts-expect-error TS18048
        const prop = node.props[propName];
        return {
          name: propName,
          type: prop.type,
          value: prop.value,
        };
      }),
      children: [],
    };

    node.children.forEach((child) => {
      // @ts-expect-error TS18048
      newElement.children.push(getExtractedElementForNode(child));
    });

    if (node.literalChildren) {
      node.literalChildren
        .sort((a, b) => a.index - b.index)
        .forEach((literalChild) => {
          // @ts-expect-error TS18048
          newElement.children.splice(literalChild.index, 0, {
            type: ExtractedElementType.LITERAL,
            literalValue: literalChild.value,
          });
        });
    }

    // Remove any null children
    node.children.filter((child) => child);

    // This is likely a component defined in the same file, skip it and just add children in a fragment
    if (
      !treeElementForNode.componentSourceImport &&
      treeElementForNode.type === JsxElementType.ComponentInstance
    ) {
      newElement = {
        type: ExtractedElementType.FRAGMENT,
        children: newElement.children,
      };
    }

    return newElement;
  };

  return {
    extractedElement: getExtractedElementForNode(navTreeNode),
  };
};

export const getComponentFileFromExtractedElements = (
  canvasId: string,
  supabaseJWT: string,
  extractedElements: ExtractedElement[]
): Promise<{
  newFileContents: string;
  dummyFilePath: string;
}> => {
  return new Promise((resolve, reject) => {
    axios
      .post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/parseAndMutate/mutate/constructNewComponent`,
        {
          extractedElements,
        },
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        }
      )
      .then((res) => {
        if (res.data) {
          if (res.data.warning) {
            toast.warning(res.data.warning);
          }

          resolve({
            newFileContents: res.data.newFileContents,
            dummyFilePath: res.data.dummyFilePath,
          });
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

/**
 * For storybook components, returns the key for the component that story is for.
 * For components, returns the key for that component.
 */
export const getLookupKeyForComponent = (element: JsxElement) => {
  if (!element) {
    return null;
  }

  if (element.type === JsxElementType.StorybookComponent) {
    return [
      element.storybookMainComponentImport?.filename,
      element.storybookMainComponentImport?.importType,
      element.storybookMainComponentImport?.name,
    ].join(":");
  }

  if (element.type === JsxElementType.ComponentInstance) {
    return [
      element.componentSourceImport?.filename,
      element.componentSourceImport?.importType,
      element.componentSourceImport?.name,
    ].join(":");
  }

  if (element.type === JsxElementType.LibraryComponent) {
    return [
      element.libraryImport?.filename,
      element.libraryImport?.importType,
      element.libraryImport?.name,
    ].join(":");
  }

  let fileNameWithoutExtension = element.filename
    ? element.filename.substring(0, element.filename.lastIndexOf("."))
    : element.filename;

  if (fileNameWithoutExtension.endsWith("index")) {
    fileNameWithoutExtension = fileNameWithoutExtension.substring(
      0,
      fileNameWithoutExtension.lastIndexOf("/")
    );
  }

  let result = [
    fileNameWithoutExtension,
    element.defaultExport
      ? ImportType.ImportDefaultSpecifier
      : ImportType.ImportSpecifier,
    element.componentName,
  ].join(":");

  return result;
};

export const getStoriesForComponent = (
  project: any,
  treeElementLookup: { [codebaseId: string]: JsxElement },
  component: JsxElement
) => {
  const componentLookupKey = getLookupKeyForComponent(component);

  return Object.keys(treeElementLookup)
    ?.map((codebaseId: string) => {
      const element: JsxElement = treeElementLookup[codebaseId];

      const filenameWithoutExtension = element.filename.replace(
        /\.[^/.]+$/,
        ""
      );

      if (
        filenameWithoutExtension.includes("tempobook/dynamic") ||
        filenameWithoutExtension.includes("tempobook/storyboards")
      ) {
        return;
      }

      if (
        project?.framework === ReactFramework.NEXT_JS &&
        filenameWithoutExtension.endsWith("pages/_app")
      ) {
        return;
      }

      if (element.type === JsxElementType.StorybookComponent) {
        const key = getLookupKeyForComponent(element);
        if (key === componentLookupKey) {
          return element;
        }
      }
    })
    .filter((element): element is JsxElement => Boolean(element));
};
